import { type ReactNode } from 'react';

import { type PlanContextFragment } from '@/common/__generated__/graphql';
import {
  getActionTaskTermContext,
  getActionTermContext,
  getIndicatorTermContext,
} from '@/common/i18n';
import { type TFunction } from '@/common/i18n';
import { ActionLink } from '@/common/links';
import ActionAttribute from '@/components/common/ActionAttribute';
import Icon from '@/components/common/Icon';

import PlanChip from '../plans/PlanChip';
import {
  AttributeTooltipContent,
  ImplementationPhaseTooltipContent,
  IndicatorsTooltipContent,
  LastUpdatedTooltipContent,
  OrganizationTooltipContent,
  PlanTooltipContent,
  ResponsiblePartiesTooltipContent,
  TasksTooltipContent,
} from './ActionTableTooltips';
import DateCell from './cells/DateCell';
import ImplementationPhaseCell from './cells/ImplementationPhaseCell';
import IndicatorsCell from './cells/IndicatorsCell';
import OrganizationCell from './cells/OrganizationCell';
import ResponsiblePartiesCell from './cells/ResponsiblePartiesCell';
import StatusCell from './cells/StatusCell';
import TasksStatusCell from './cells/TasksStatusCell';
import UpdatedAtCell from './cells/UpdatedAtCell';
import type {
  ActionListAction,
  ActionListPlan,
  ColumnBlock,
  ColumnConfig,
} from './dashboard.types';
import { getActionPlanImage, getActionPlanName, isFromOtherPlan } from './dashboard.utils';

const getPlanUrl = (
  mergedWith: ActionListAction['mergedWith'],
  actionPlan: ActionListAction['plan'],
  planId: string
) => {
  if (mergedWith && mergedWith?.plan.id !== planId) return mergedWith.plan.viewUrl;
  if (actionPlan && actionPlan.id !== planId) return actionPlan.viewUrl;
  return undefined;
};

interface Column {
  sortable?: boolean;
  headerKey?: keyof ActionListAction;
  rowHeader?: boolean;
  renderTooltipContent?: (
    t: TFunction,
    action: ActionListAction,
    plan: PlanContextFragment,
    attribute?: ColumnConfig['attributeType']
  ) => ReactNode;
  /**
   * The tooltip repeats the cell's text, so screen readers don't need it as
   * hidden text in the cell (see ActionTableRow).
   */
  tooltipRepeatsCell?: boolean;
  headerClassName?: string;
  cellClassName?: string;
  renderHeader: (t: TFunction, plan: PlanContextFragment, label?: string | null) => ReactNode;
  renderCell: (
    t: TFunction,
    action: ActionListAction,
    plan: PlanContextFragment,
    planViewUrl?: string | null,
    attribute?: ColumnConfig['attributeType']
  ) => ReactNode;
}

export const COLUMN_CONFIG: { [key in ColumnBlock]: Column } = {
  // TODO: Add tooltip?
  StatusColumnBlock: {
    renderHeader: (t, _, label) => label || t('status'),
    renderCell: (_, action, plan) => <StatusCell action={action} plan={plan} />,
  },

  IdentifierColumnBlock: {
    renderHeader: (t, _, label) => <abbr>{label || t('action-id')}</abbr>,
    renderCell: (_, action) => action.identifier,
  },

  NameColumnBlock: {
    sortable: true,
    headerKey: 'name',
    rowHeader: true,
    renderHeader: (t, plan, label) => label || t('action-name-title', getActionTermContext(plan)),
    renderCell: (_, action, plan, planViewUrl) => {
      const { mergedWith } = action;
      const fromOtherPlan = action.plan ? action.plan.id !== plan.id : false;
      const mergedWithActionFromOtherPlan = mergedWith != null && mergedWith.plan.id !== plan.id;
      return (
        <ActionLink
          action={action}
          viewUrl={action.mergedWith?.viewUrl ?? action.viewUrl}
          planUrl={getPlanUrl(mergedWith, action.plan, plan.id) ?? planViewUrl}
          crossPlan={fromOtherPlan || mergedWithActionFromOtherPlan}
        >
          {action.name}
        </ActionLink>
      );
    },
  },

  OrganizationColumnBlock: {
    headerClassName: 'logo-column',
    renderHeader: (t, _, label) => label || t('logo'),
    renderCell: (_, action) => <OrganizationCell action={action} />,
    renderTooltipContent: (_, action, plan) => {
      const fromOtherPlan = action.plan ? action.plan.id !== plan.id : false;
      return (
        <OrganizationTooltipContent
          action={action}
          plan={(fromOtherPlan ? action.plan : plan) as ActionListPlan}
        />
      );
    },
  },

  ImplementationPhaseColumnBlock: {
    sortable: true,
    headerKey: 'implementationPhase',
    renderHeader: (t, _, label) => label || t('action-implementation-phase'),
    renderCell: (_, action, plan) => {
      const fromOtherPlan = action.plan ? action.plan.id !== plan.id : false;
      return (
        <ImplementationPhaseCell
          action={action}
          plan={(fromOtherPlan ? action.plan : plan) as PlanContextFragment}
        />
      );
    },
    renderTooltipContent: (_, action, plan) => {
      const fromOtherPlan = action.plan ? action.plan.id !== plan.id : false;
      return (
        <ImplementationPhaseTooltipContent
          action={action}
          plan={(fromOtherPlan ? action.plan : plan) as ActionListPlan}
        />
      );
    },
  },

  TasksColumnBlock: {
    renderHeader: (t, plan, label) => label || t('action-tasks', getActionTaskTermContext(plan)),
    renderCell: (_, action, plan) => <TasksStatusCell action={action} plan={plan} />,
    renderTooltipContent: (_, action, plan) => {
      const fromOtherPlan = action.plan ? action.plan.id !== plan.id : false;
      return (
        <TasksTooltipContent
          action={action}
          plan={(fromOtherPlan ? action.plan : plan) as ActionListPlan}
        />
      );
    },
  },

  ResponsiblePartiesColumnBlock: {
    renderHeader: (t, _, label) => label || t('action-responsibles-short'),
    renderCell: (_, action) => <ResponsiblePartiesCell action={action} />,
    renderTooltipContent: (_, action, plan) => {
      const fromOtherPlan = action.plan ? action.plan.id !== plan.id : false;
      return (
        <ResponsiblePartiesTooltipContent
          action={action}
          plan={(fromOtherPlan ? action.plan : plan) as ActionListPlan}
        />
      );
    },
  },

  IndicatorsColumnBlock: {
    renderHeader: (t, plan, label) => label || t('indicators', getIndicatorTermContext(plan)),
    renderCell: (_, action) => <IndicatorsCell action={action} />,
    renderTooltipContent: (_, action, plan) => (
      <IndicatorsTooltipContent action={action} plan={plan} />
    ),
  },

  UpdatedAtColumnBlock: {
    sortable: true,
    headerKey: 'updatedAt',
    renderHeader: (t, _, label) => label || t('action-last-updated'),
    renderCell: (_, action) => <UpdatedAtCell action={action} />,
    renderTooltipContent: (_, action) => <LastUpdatedTooltipContent action={action} />,
  },

  StartDateColumnBlock: {
    sortable: true,
    headerKey: 'startDate',
    renderHeader: (t, _, label) => label || t('action-start-date'),
    renderCell: (_, action) => <DateCell date={action.startDate} />,
  },

  EndDateColumnBlock: {
    sortable: true,
    headerKey: 'endDate',
    renderHeader: (t, _, label) => label || t('action-end-date'),
    renderCell: (_, action) => <DateCell date={action.endDate} />,
  },

  ScheduleContinuousColumnBlock: {
    sortable: true,
    headerKey: 'scheduleContinuous',
    renderHeader: (t, _, label) => label || t('action-continuous'),
    renderCell: (t, action) => (
      <Icon
        name={action.scheduleContinuous ? 'check-circle' : 'circle-outline'}
        alt={action.scheduleContinuous ? t('action-continuous') : t('action-not-continuous')}
      />
    ),
    renderTooltipContent: (t, action) =>
      action.scheduleContinuous ? t('action-continuous') : t('action-not-continuous'),
    tooltipRepeatsCell: true,
  },

  FieldColumnBlock: {
    renderHeader: (t, _, label) => label,
    renderCell: (_, action, __, ___, attributeType) => {
      if (!attributeType) return null;
      const attributeContent = action.attributes.find((a) => a.type.id === attributeType.id);
      if (!attributeContent) return null;
      return (
        <ActionAttribute
          {...({
            attribute: attributeContent,
            attributeType,
            notitle: true,
            variant: 'minimized',
          } as Parameters<typeof ActionAttribute>[0])}
        />
      );
    },
    renderTooltipContent: (_, action, __, attributeType) => {
      if (!attributeType) return null;
      const attributeContent = action.attributes.find((a) => a.type.id === attributeType.id);
      if (!attributeContent) return null;
      return <AttributeTooltipContent attribute={attributeContent} attributeType={attributeType} />;
    },
    tooltipRepeatsCell: true,
  },
  PlanColumnBlock: {
    renderHeader: (t, _, label) => label || t('filter-plan'),
    renderCell: (_, action, plan) => {
      /* Only the plan the action belongs to may identify the row; the viewed
       * plan's short identifier would be misleading for a related action. */
      const shortIdentifier = isFromOtherPlan(action.plan, plan)
        ? action.plan?.shortIdentifier
        : plan?.shortIdentifier;

      return (
        shortIdentifier || (
          <PlanChip
            planImage={getActionPlanImage(action.plan, plan)}
            imageAlt={getActionPlanName(action.plan, plan)}
            size="lg"
          />
        )
      );
    },
    renderTooltipContent: (_, action, plan) => <PlanTooltipContent action={action} plan={plan} />,
  },
};
